import { useState, useCallback } from 'react';
import { MindspaceDashboardCard } from '@/components/features/mindspace/cards/MindspaceDashboardCard';
import { DotGrid, DotClickInfo } from '@/components/core/animations/DotGrid';
import { ConceptPopover } from '@/components/features/mindspace/concepts/ConceptPopover';
import { useIsDark } from '@/hooks/ui/useIsDark';

// DotGrid paints to a <canvas> with literal rgb() colors, so `dark:` classes
// can't reach it - we branch the palette in JS instead. Light stays the
// original indigo (base grey dots, indigo hover/concept dots); dark neutralises
// the base dots and swaps the accents to the lavender --primary so the grid
// matches the rest of the dark theme instead of reading indigo.
const DOT_COLORS = {
  light: {
    base: 'rgb(209, 213, 219)', // gray-300
    active: 'rgb(129, 140, 248)', // brand-400
    highlight: 'rgb(99, 102, 241)', // brand-500
  },
  dark: {
    base: 'rgb(82, 82, 82)', // neutral-600
    active: 'rgb(196, 181, 253)', // violet-300 (brighter on hover)
    highlight: 'rgb(172, 139, 249)', // --primary (lavender)
  },
} as const;

// Mock concepts data
const mockConcepts = [
  {
    name: 'Neural Networks',
    description:
      'A computing system inspired by biological neural networks that learns from data.',
  },
  {
    name: 'Machine Learning',
    description:
      'A subset of AI that enables systems to learn and improve from experience.',
  },
  {
    name: 'Deep Learning',
    description:
      'A type of machine learning based on artificial neural networks with multiple layers.',
  },
  {
    name: 'Natural Language Processing',
    description:
      'The ability of computers to understand and process human language.',
  },
  {
    name: 'Computer Vision',
    description:
      'A field of AI that trains computers to interpret visual information.',
  },
  {
    name: 'Reinforcement Learning',
    description:
      'A type of ML where agents learn by interacting with an environment.',
  },
  {
    name: 'Gradient Descent',
    description:
      'An optimization algorithm used to minimize the loss function in ML models.',
  },
  {
    name: 'Backpropagation',
    description:
      'An algorithm for training neural networks by computing gradients.',
  },
  {
    name: 'Convolutional Networks',
    description:
      'Neural networks designed for processing structured grid data like images.',
  },
  {
    name: 'Transformers',
    description:
      'A deep learning architecture using self-attention mechanisms.',
  },
  {
    name: 'Attention Mechanism',
    description:
      'A technique that allows models to focus on relevant parts of input.',
  },
  {
    name: 'Embeddings',
    description:
      'Dense vector representations of discrete data like words or categories.',
  },
  {
    name: 'Loss Functions',
    description:
      'Functions that measure how well a model predictions match the target.',
  },
  {
    name: 'Activation Functions',
    description:
      'Non-linear functions that determine the output of neural network nodes.',
  },
  {
    name: 'Regularization',
    description:
      'Techniques to prevent overfitting by adding constraints to models.',
  },
];

// Scattered dot indices that have concepts (15 dots spread across grid)
const conceptDotIndices = new Set([
  2, 7, 11, 16, 23, 29, 34, 41, 48, 55, 63, 70, 78, 85, 93,
]);

// Map from dot index to concept index
const dotToConceptMap = new Map<number, number>();
Array.from(conceptDotIndices).forEach((dotIndex, i) => {
  dotToConceptMap.set(dotIndex, i);
});

interface PopoverState {
  isVisible: boolean;
  concept: { name: string; description: string } | null;
  position: { x: number; y: number };
}

export function KnowledgeArchiveCard() {
  const isDark = useIsDark();
  const dots = isDark ? DOT_COLORS.dark : DOT_COLORS.light;
  const [popover, setPopover] = useState<PopoverState>({
    isVisible: false,
    concept: null,
    position: { x: 0, y: 0 },
  });

  const handleDotClick = useCallback((dot: DotClickInfo, event: MouseEvent) => {
    if (!conceptDotIndices.has(dot.index)) return;

    const conceptIndex = dotToConceptMap.get(dot.index);
    if (conceptIndex === undefined) return;

    const concept = mockConcepts[conceptIndex];
    if (!concept) return;

    setPopover({
      isVisible: true,
      concept,
      position: { x: event.clientX, y: event.clientY },
    });
  }, []);

  const handleClosePopover = useCallback(() => {
    setPopover(prev => ({ ...prev, isVisible: false }));
  }, []);

  return (
    <MindspaceDashboardCard
      title='Knowledge Archive'
      subtitle='Browse all your concepts'
      actionPath='/mindspace/knowledge-archive'
      actionLabel='Explore'
    >
      <div className='h-full min-h-[180px] w-full'>
        <DotGrid
          dotSize={8}
          gap={28}
          baseColor={dots.base}
          activeColor={dots.active}
          proximity={80}
          speedTrigger={100}
          shockRadius={120}
          shockStrength={3}
          maxSpeed={5000}
          resistance={750}
          returnDuration={1.2}
          onDotClick={handleDotClick}
          highlightedDots={conceptDotIndices}
          highlightColor={dots.highlight}
        />
      </div>

      {/* Concept Popover */}
      {popover.concept && (
        <ConceptPopover
          title={popover.concept.name}
          description={popover.concept.description}
          position={popover.position}
          isVisible={popover.isVisible}
          onClose={handleClosePopover}
        />
      )}
    </MindspaceDashboardCard>
  );
}
